import React, { useEffect, useState, useRef } from 'react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createCostCentre,
  updateCostCentre,
  deleteCostCentre,
  getCostCentre,
  checkCostCentreDuplicate,
} from '../api/cost-centreAPI';
import { fetchData } from '../utils/apiHelpers';
import $ from 'jquery';
import { Icon } from '@iconify/react';

const CostCentreListLayer = () => {
  const userProfile = useUserProfile();
  const [costCentreList, setCostCentreList] = useState([]);
  const [newCentre, setNewCentre] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchCentres = () => {
    fetchData(getCostCentre, {}, setCostCentreList);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newCentre.costCentreCode?.trim()) newErrors.costCentreCode = 'Code is required';
    if (!newCentre.costCenterDescription?.trim()) newErrors.costCenterDescription = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchCentres();
  }, []);

  useEffect(() => {
    const handleRefresh = () => {
      fetchCentres();
    };

    window.addEventListener('cost-centres-updated', handleRefresh);
    return () => window.removeEventListener('cost-centres-updated', handleRefresh);
  }, []);

  useEffect(() => {
    if (dtRef.current) dtRef.current.destroy();
    if (costCentreList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({ pageLength: 25, dom: 'rtip' });
    }
    return () => dtRef.current?.destroy(true);
  }, [costCentreList]);

  useEffect(() => {
    dtRef.current?.search(searchTerm).draw();
  }, [searchTerm]);

  const handleSave = async () => {
    if (!validateForm()) return;
    const dup = await checkCostCentreDuplicate(
      newCentre.costCentreCode,
      newCentre.costCenterDescription,
      editingId,
    );
    const dupErrors = {};
    if (dup.codeExists) dupErrors.costCentreCode = 'Code already exists';
    if (dup.nameExists)
      dupErrors.costCenterDescription = 'Description already exists';
    if (Object.keys(dupErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...dupErrors }));
      return;
    }

    const clean = {};
    for (const key in newCentre) {
      if (newCentre[key] !== null && typeof newCentre[key] !== 'object') {
        clean[key] = newCentre[key];
      }
    }
    editingId
      ? await updateCostCentre(editingId, clean)
      : await createCostCentre(clean);
    setShowModal(false);
    setEditingId(null);
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    setNewCentre({});
    await fetchCentres();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete cost centre?')) return;
    await deleteCostCentre(id);
    if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; }
    await fetchCentres();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCentre(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between p-3">
        <input type="text" className="form-control w-auto" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        {userProfile?.privileges?.includes('cost-centres:create') && (
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={() => { setNewCentre({}); setEditingId(null); setShowModal(true); }}>
            <Icon icon="ic:baseline-plus" className="icon text-xl" /> Add Cost Centre
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table ref={tableRef} className="table bordered-table mb-0">
            <thead><tr><th>Code</th><th>Description</th><th>Actions</th></tr></thead>
            <tbody>
              {costCentreList.map(item => (
                <tr key={item.id}>
                  <td><strong>{item.costCentreCode}</strong></td>
                  <td>{item.costCenterDescription}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('cost-centres:update') && (
                        <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => { setNewCentre(item); setEditingId(item.id); setShowModal(true); }}>
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('cost-centres:delete') && (
                        <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}>
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg"><div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">{editingId ? 'Edit Cost Centre' : 'New Cost Centre'}</h5><button className="btn-close" onClick={() => setShowModal(false)} /></div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Cost Centre Code *</label>
                <input className={`form-control ${errors.costCentreCode ? 'is-invalid' : ''}`} name="costCentreCode" value={newCentre.costCentreCode || ''} onChange={handleInputChange} />
                {errors.costCentreCode && <div className="invalid-feedback">{errors.costCentreCode}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Description *</label>
                <input className={`form-control ${errors.costCenterDescription ? 'is-invalid' : ''}`} name="costCenterDescription" value={newCentre.costCenterDescription || ''} onChange={handleInputChange} />
                {errors.costCenterDescription && <div className="invalid-feedback">{errors.costCenterDescription}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
              <button className="btn btn-primary" onClick={handleSave}>Save</button>
            </div>
          </div></div>
        </div>
      )}
    </div>
  );
};

export default CostCentreListLayer;
